import React from 'react';
import { Sparkles, Target, Heart, Globe, Users, BookOpen } from 'lucide-react';
import { aboutContent, siteConfig } from '../data/mock';
import aboutHero from "../assets/images/aboutBG.webp";

const About = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section
        className="relative py-28 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `
    linear-gradient(
      to bottom,
      rgba(255,247,230,0.65),
      rgba(192,119,51,0.9)
    ),
    url(${aboutHero})
  `,
        }}

      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-5 py-2 shadow-lg">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium uppercase tracking-wide">
                About Us
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold font-serif leading-tight">
              Vidushi Arundhati Centre for Indian Knowledge Systems
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground">
              Maitreyi College, University of Delhi
            </p>
          </div>
        </div>
      </section>


      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-6 font-serif">
                  Introduction
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                  {aboutContent.introduction}
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  We bridge ancient Indian epistemologies with contemporary scientific, social, and technological paradigms, fostering a culture of critical inquiry, creativity, and innovation rooted in value-based knowledge and sustainable practices.
                </p>
              </div>
              <div className="relative">
                <img
                  src={'/assets/AboutImage.jpeg'}
                  alt="Indian heritage"
                  className="rounded-2xl shadow-2xl w-full h-96 object-cover hover-lift"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inspiration: Vidushi Arundhati */}
      {/* <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground font-serif">
                  {aboutContent.inspiration.title}
                </h2>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {aboutContent.inspiration.description}
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Vision */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-foreground font-serif">
                Our Vision
              </h2>
            </div>
            <p className="text-xl text-foreground leading-relaxed font-medium bg-gradient-to-br from-secondary/50 to-primary/10 p-8 rounded-2xl border-l-4 border-primary">
              {aboutContent.vision}
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-secondary/20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground font-serif">
                  Our Mission
                </h2>
              </div>
              <p className="text-muted-foreground text-lg">
                Seven pillars guiding our journey
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {aboutContent.mission.map((item, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-lg hover-lift border-l-4 border-primary"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-primary font-bold">{index + 1}</span>
                    </div>
                    <p className="text-foreground leading-relaxed">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground font-serif">
                  Core Values
                </h2>
              </div>
              <p className="text-muted-foreground text-lg">
                The principles that guide our work
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {aboutContent.coreValues.map((item, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-secondary/30 to-white p-6 rounded-xl shadow-lg hover-lift text-center group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                    <Sparkles className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground group-hover:text-primary transition-colors">
                    {item.value}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NEP 2020 Alignment */}
      <section className="py-16 bg-gradient-to-br from-primary to-orange-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold font-serif">
                Alignment with NEP 2020
              </h2>
            </div>
            <p className="text-xl text-white/90 leading-relaxed">
              {aboutContent.nep2020}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
